import type { AddGroupLifeInsuranceDto } from "@/models/dto/groupLifeInsurance.dto";
import type { SaveComplianceEvaluationDto } from "@/models/dto/save-compliance-evaluation.dto";
import ComplianceService, {
  type ComplianceCheckResult,
} from "@/services/compliance/compliance.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { useMemo } from "react";
import type { ECertificateStatus } from "@/constants/enums/certificate-status.enum";
import type { GenerateCertificateDto } from "@/models/dto/generate-certificate.dto";
import type { RespondToEvaluationDto } from "@/models/dto/respond-to-evaluation.dto";
import type { PaginatedResponse } from "@/models/responses/base/baseResponse.response";
import type { EmployeeContribution } from "@/models/responses/compliance/employer-contributions.response";
import type { CurrentYearFilterDto } from "@/models/dto/current-year-filter.dto";
import type { PenaltyStatusFilterDto } from "@/models/dto/penalty-status-filter.dto";
import type { BaseResponse } from "@/models/responses/base/baseResponse.response";
import type { ComplianceEvaluation } from "@/models/responses/compliance/complianceEvaluation.response";
import { PencomConstants } from "@/constants/PencomConstants";
import type { PaymentScheduleResponse } from "@/models/responses/compliance/payment-schedule.response";

export const useGetEmployeeStats = () => {
  return useQuery({
    queryKey: ["employee-stats"],
    queryFn: () => ComplianceService.getEmployeeStats(),
  });
};

export const useGetContributionStats = () => {
  return useQuery({
    queryKey: ["contribution-stats"],
    queryFn: () => ComplianceService.getContributionStats(),
  });
};

export const useGetGroupLifeInsurance = () => {
  return useQuery({
    queryKey: ["group-life-insurance"],
    queryFn: () => ComplianceService.getGroupLifeInsurance(),
  });
};

export const useAddGroupLifeInsurance = () => {
  const queryClient = useQueryClient();
  const { mutate: evaluateCompliance } = useEvaluateCompliance();

  return useMutation({
    mutationFn: (dto: AddGroupLifeInsuranceDto) =>
      ComplianceService.addGroupLifeInsurance(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-life-insurance"] });
      evaluateCompliance();
    },
  });
};

export const useEvaluateCompliance = () => {
  return useMutation<ComplianceEvaluation, Error, void>({
    mutationFn: () => ComplianceService.evaluateCompliance(),
  });
};

export const useSaveComplianceEvaluation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: SaveComplianceEvaluationDto) =>
      ComplianceService.saveComplianceEvaluation(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["compliance-evaluation"] });
    },
  });
};

export const useGetComplianceEvaluation = () => {
  return useQuery<ComplianceEvaluation, Error>({
    queryKey: ["compliance-evaluation"],
    queryFn: () => ComplianceService.getComplianceEvaluation(),
  });
};

export const useComplianceCheckResults = (
  complianceEvaluationData?:
    | ComplianceEvaluation
    | BaseResponse<ComplianceEvaluation>
) => {
  const evaluation = useMemo<ComplianceEvaluation | undefined>(() => {
    if (typeof complianceEvaluationData === "undefined") return undefined;

    // Some backends wrap evaluation in `{ data: ... }` while others return it directly.
    if (
      typeof complianceEvaluationData === "object" &&
      complianceEvaluationData !== null &&
      "data" in complianceEvaluationData
    ) {
      return (complianceEvaluationData as BaseResponse<ComplianceEvaluation>)
        .data;
    }

    return complianceEvaluationData as ComplianceEvaluation;
  }, [complianceEvaluationData]);

  const checkResults = useMemo<ComplianceCheckResult[]>(() => {
    return ComplianceService.parseDecisionTrace(
      evaluation?.decision_trace ?? [],
      evaluation?.failed_conditions,
      evaluation?.blocks
    );
  }, [evaluation]);

  const canProceed = useMemo(() => {
    if (typeof evaluation?.can_proceed === "boolean")
      return evaluation.can_proceed;
    return checkResults.every((check) => check.status === "passed");
  }, [evaluation, checkResults]);

  const hasHardBlock = useMemo(() => {
    return evaluation?.blocks?.some((b) => b.type === "HARD_BLOCK") ?? false;
  }, [evaluation]);

  const hasSoftBlock = useMemo(() => {
    return evaluation?.blocks?.some((b) => b.type === "SOFT_BLOCK") ?? false;
  }, [evaluation]);

  const hasOutstandingPenaltySoftBlock = useMemo(() => {
    const isPenaltySoftBlock = (code?: string, type?: string) =>
      type === "SOFT_BLOCK" &&
      (code === PencomConstants.PCC_CHECKLIST_STATUS.OUTSTANDING_PENALTY ||
        code === "PENALTY_OUTSTANDING");

    const fromBlocks =
      evaluation?.blocks?.some((b) =>
        isPenaltySoftBlock(b.code, b.type)
      ) ?? false;

    const fromCheckResults = checkResults.some((r) =>
      isPenaltySoftBlock(r.blockCode, r.blockType)
    );

    return fromBlocks || fromCheckResults;
  }, [evaluation, checkResults]);

  const hasMissingContributionsSoftBlock = useMemo(() => {
    const isMissingContributionsSoftBlock = (code?: string, type?: string) =>
      type === "SOFT_BLOCK" &&
      code === PencomConstants.PCC_CHECKLIST_STATUS.CONTRIBUTIONS_INCOMPLETE;

    const fromBlocks =
      evaluation?.blocks?.some((b) =>
        isMissingContributionsSoftBlock(b.code, b.type)
      ) ?? false;

    const fromCheckResults = checkResults.some((r) =>
      isMissingContributionsSoftBlock(r.blockCode, r.blockType)
    );

    return fromBlocks || fromCheckResults;
  }, [evaluation, checkResults]);

  const hasBlocks = useMemo(() => {
    return evaluation?.blocks?.length ? true : false;
  }, [evaluation]);

  return {
    checkResults,
    canProceed,
    hasBlocks,
    hasHardBlock,
    hasSoftBlock,
    hasOutstandingPenaltySoftBlock,
    hasMissingContributionsSoftBlock,
  };
};

export const useGetCertificates = (status: ECertificateStatus) => {
  return useQuery({
    queryKey: ["certificates", status || undefined],
    queryFn: () => ComplianceService.getCertificates(status),
  });
};

export const useGenerateCertificate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: GenerateCertificateDto) =>
      ComplianceService.generateCertificate(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificates"] });
    },
  });
};

/**
 * Refreshes a certificate ahead of download — the backend re-renders the PDF
 * only if the figures it displays have moved since the last render. The cached
 * certificate list is only invalidated when a re-render actually happened, so an
 * up-to-date certificate costs no refetch.
 */
export const useRefreshCertificate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ComplianceService.refreshCertificate(id),
    onSuccess: (result) => {
      if (result?.regenerated) {
        queryClient.invalidateQueries({ queryKey: ["certificates"] });
      }
    },
  });
};

export const useGetCertificateById = (id: string) => {
  return useQuery({
    queryKey: ["certificate", id],
    queryFn: () => ComplianceService.getCertificateById(id),
    enabled: !!id,
  });
};

export const useGetEvaluationComments = (evaluationId: string) => {
  return useQuery({
    queryKey: ["evaluation-comments", evaluationId],
    queryFn: () => ComplianceService.getEvaluationComments(evaluationId),
    enabled: !!evaluationId,
  });
};

export const useRespondToEvaluation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: RespondToEvaluationDto) =>
      ComplianceService.respondToEvaluation(dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["evaluation-comments", variables.evaluationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["compliance-evaluation"],
      });
    },
  });
};

export const useGetContributionTotal = (employerCode: string) => {
  return useQuery({
    queryKey: ["contribution-total", employerCode],
    queryFn: () => ComplianceService.getContributionTotal(employerCode),
    enabled: !!employerCode,
  });
};

export const useUpdateGroupLifeInsurance = () => {
  const queryClient = useQueryClient();
  const { mutate: evaluateCompliance } = useEvaluateCompliance();
  return useMutation({
    mutationFn: (dto: AddGroupLifeInsuranceDto) =>
      ComplianceService.updateGroupLifeInsurance(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-life-insurance"] });
      evaluateCompliance();
    },
  });
};

export const useGetEmployeeContributions = (
  page: number = 1,
  limit: number = 10
) => {
  return useQuery<PaginatedResponse<EmployeeContribution>>({
    queryKey: ["employee-contributions", page, limit],
    queryFn: () =>
      ComplianceService.getEmployeeContributions({
        page,
        limit,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Fetches employee contributions for a given year and month (paginated).
 * @returns Query with data (EmployeeContribution[]), pagination (page, limit, total, totalPages, hasNext, hasPrevious)
 */
export const useGetContributionByMonth = (
  year: string,
  month: string,
  page: number = 1,
  limit: number = 10,
  contributionType?: string
) => {
  return useQuery<PaginatedResponse<EmployeeContribution>>({
    queryKey: [
      "contribution-by-month",
      year,
      month,
      page,
      limit,
      contributionType ?? null,
    ],
    queryFn: () => {
      return ComplianceService.getContributionByMonth({
        year,
        month,
        page,
        limit,
        contributionType,
      });
    },
    enabled: !!year && !!month,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook to fetch all employee contributions across all pages
export const useGetAllEmployeeContributions = () => {
  return useQuery<EmployeeContribution[]>({
    queryKey: ["all-employee-contributions"],
    queryFn: async () => {
      const limit = 10; // Max allowed by API
      let allData: EmployeeContribution[] = [];

      // Fetch first page to get total pages
      const firstPageResponse =
        await ComplianceService.getEmployeeContributions({
          page: 1,
          limit,
        });

      allData = [...firstPageResponse.data];
      const totalPages = firstPageResponse.pagination.totalPages;

      // Fetch remaining pages if any
      const fetchPromises = [];
      for (let page = 2; page <= totalPages; page++) {
        fetchPromises.push(
          ComplianceService.getEmployeeContributions({
            page,
            limit,
          })
        );
      }

      const remainingPages = await Promise.all(fetchPromises);
      remainingPages.forEach((response) => {
        allData = [...allData, ...response.data];
      });

      return allData;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useGetEmployeeCount = () => {
  return useQuery({
    queryKey: ["employee-count"],
    queryFn: () => ComplianceService.getEmployeeCount(),
  });
};

export const useGetCurrentMonthlyReports = () => {
  return useQuery({
    queryKey: ["current-monthly-reports"],
    queryFn: () => ComplianceService.getCurrentMonthReport(),
  });
};

export const useGetCurrentYearReports = (dto?: CurrentYearFilterDto) => {
  return useQuery({
    queryKey: ["current-year-reports", dto],
    queryFn: () => ComplianceService.getCurrentYearReports(dto),
  });
};

export const useGetPenaltyStatus = (dto?: PenaltyStatusFilterDto) => {
  return useQuery({
    queryKey: ["penalty-status", dto],
    queryFn: () =>
      ComplianceService.getPenaltyStatus(dto?.day ?? new Date().toISOString()),
  });
};

export const useGetTasks = () => {
  return useQuery({
    queryKey: ["tasks"],
    queryFn: () => ComplianceService.getTasks(),
  });
};

export const useGetPenalties = () => {
  const { currentEmployer } = useAuthStore();
  const employerCode = currentEmployer?.employerCode;
  return useQuery({
    queryKey: ["penalties", employerCode],
    queryFn: () => ComplianceService.getPenalties(),
    enabled: !!employerCode,
  });
};

export const useGetBanners = () => {
  return useQuery({
    queryKey: ["banners"],
    queryFn: () => ComplianceService.getBanners(),
  });
};

export const useDownloadPenaltiesSchedulePdf = () => {
  return useMutation({
    mutationFn: () => ComplianceService.getPenaltiesSchedulePdf(),
  });
};

export const useGetPenaltiesBreakdown = (
  month: string,
  year: string,
  enabled = true
) => {
  return useQuery({
    queryKey: ["penalties-breakdown", month, year],
    queryFn: () => ComplianceService.getPenaltiesBreakdown(month, year),
    enabled: enabled && month.length > 0 && year.length > 0,
  });
};

export const useDownloadPenaltiesScheduleXlsx = () => {
  return useMutation({
    mutationFn: () => ComplianceService.getPenaltiesScheduleXlsx(),
  });
};

export const useDownloadContributionsScheduleXlsx = () => {
  return useMutation({
    mutationFn: () => ComplianceService.getContributionsScheduleXlsx(),
  });
};

export const useGetPaymentSchedule = () => {
  return useQuery<PaymentScheduleResponse>({
    queryKey: ["payment-schedule"],
    queryFn: () => ComplianceService.getPaymentSchedule(),
  });
};

export const useAcknowledgeCertificateDisclaimer = () => {
  return useMutation({
    mutationFn: ({
      id,
      disclaimerVersion,
    }: {
      id: string;
      disclaimerVersion: string;
    }) => ComplianceService.acknowledgeCertificateDisclaimer(id, disclaimerVersion),
  });
};
